import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Plus, CircleNotch } from "@phosphor-icons/react";
import { apiGet, apiSend } from "@/lib/api-error";
import { toast } from "@/lib/toast";
import { wallClockToUtc, utcToWallClock } from "@/lib/timezone";
import { sameSessionSchedule } from "@/lib/group-therapy-state";
import {
  GroupTherapyForm,
  emptyGroupTherapyForm,
  type GroupTherapyFormData,
} from "@/components/group-therapy/GroupTherapyForm";
import {
  GroupTherapyCard,
  type CycleSummary,
} from "@/components/group-therapy/GroupTherapyCard";
import { GroupTherapyParticipantsDialog } from "@/components/group-therapy/GroupTherapyParticipantsDialog";

export const Route = createFileRoute("/dashboard/therapist/group-therapies")({
  component: TherapistGroupTherapies,
});

function TherapistGroupTherapies() {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<GroupTherapyFormData>({ ...emptyGroupTherapyForm });
  // Raspored kakav je učitan za uređivanje (UTC). Termini se šalju samo kad se
  // raspored stvarno promijeni, jer ih server nakon prve prijave ne dopušta mijenjati.
  const [loadedSchedule, setLoadedSchedule] = useState<string[] | null>(null);
  const [participantsOf, setParticipantsOf] = useState<CycleSummary | null>(null);

  const { data, isLoading } = useQuery<{ cycles: CycleSummary[] }>({
    queryKey: ["therapist", "group-therapies"],
    queryFn: () =>
      apiGet<{ cycles: CycleSummary[] }>(
        "/api/group-therapies/mine",
        "Greška pri učitavanju grupnih terapija"
      ),
  });

  const cycles = data?.cycles ?? [];

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ["therapist", "group-therapies"] });

  const buildPayload = (formData: GroupTherapyFormData, baseline: string[] | null = null) => {
    const sessions = formData.sessions.map((s) => ({
      scheduledAt: wallClockToUtc(s.date, s.time),
    }));
    const scheduleChanged =
      !baseline || !sameSessionSchedule(sessions.map((s) => s.scheduledAt), baseline);
    return {
      name: formData.name.trim(),
      description: formData.description.trim(),
      price: formData.price,
      maxParticipants: formData.maxParticipants,
      durationMinutes: formData.durationMinutes,
      ...(scheduleChanged ? { sessions } : {}),
    };
  };

  const createMutation = useMutation({
    mutationFn: (formData: GroupTherapyFormData) =>
      apiSend(
        "/api/group-therapies",
        "POST",
        buildPayload(formData),
        "Greška pri kreiranju grupne terapije"
      ),
    onSuccess: () => {
      invalidate();
      resetForm();
      toast.success("Grupna terapija je kreirana");
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, formData }: { id: string; formData: GroupTherapyFormData }) =>
      apiSend(
        `/api/group-therapies/${id}`,
        "PATCH",
        buildPayload(formData, loadedSchedule),
        "Greška pri ažuriranju grupne terapije"
      ),
    onSuccess: () => {
      invalidate();
      resetForm();
      toast.success("Izmjene su sačuvane");
    },
  });

  const resetForm = () => {
    setForm({ ...emptyGroupTherapyForm });
    setEditingId(null);
    setLoadedSchedule(null);
    setShowForm(false);
  };

  const startEdit = async (cycleId: string) => {
    try {
      const data = await apiGet<{
        cycle: {
          id: string;
          name: string;
          description: string;
          price: number;
          maxParticipants: number;
          durationMinutes: number;
        };
        sessions: Array<{ id: string; scheduledAt: string; cancelledAt: string | null }>;
      }>(`/api/group-therapies/${cycleId}/mine`, "Greška pri učitavanju");
      // Decompose UTC into Sarajevo wall-clock. utcToWallClock pins the TZ
      // to Europe/Sarajevo so therapists editing from any browser see and
      // round-trip the same wall-clock time as their bookings use.
      const activeSessions = data.sessions.filter((s) => !s.cancelledAt);
      const sessions = activeSessions.map((s) => utcToWallClock(s.scheduledAt));
      setForm({
        name: data.cycle.name,
        description: data.cycle.description,
        price: data.cycle.price,
        maxParticipants: data.cycle.maxParticipants,
        durationMinutes: data.cycle.durationMinutes,
        sessions,
      });
      setLoadedSchedule(activeSessions.map((s) => s.scheduledAt));
      setEditingId(cycleId);
      setShowForm(true);
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const handleSubmit = () => {
    if (editingId) {
      updateMutation.mutate({ id: editingId, formData: form });
    } else {
      createMutation.mutate(form);
    }
  };

  const cancelMutation = useMutation({
    mutationFn: (id: string) =>
      apiSend(
        `/api/group-therapies/${id}/cancel`,
        "POST",
        undefined,
        "Greška pri otkazivanju ciklusa"
      ),
    onSuccess: () => {
      invalidate();
      toast.success("Ciklus je otkazan");
    },
  });

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <CircleNotch className="size-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl space-y-8 pb-14">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold leading-tight tracking-tight text-foreground sm:text-4xl" style={{ fontFamily: "var(--font-heading)" }}>
            Grupne terapije
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Kreirajte ciklus sa više termina i fiksnom cijenom za cijeli ciklus.
          </p>
        </div>
        {!showForm && (
          <Button onClick={() => setShowForm(true)}>
            <Plus className="size-4" /> Nova grupna terapija
          </Button>
        )}
      </div>

      {showForm && (
        <GroupTherapyForm
          form={form}
          setForm={setForm}
          editingId={editingId}
          isSaving={createMutation.isPending || updateMutation.isPending}
          onSubmit={handleSubmit}
          onCancel={resetForm}
        />
      )}

      {cycles.length === 0 && !showForm ? (
        <div className="rounded-lg border border-dashed border-[#e6dfcf] p-10 text-center">
          <p className="text-sm text-muted-foreground">
            Još nemate kreiranih grupnih terapija.
          </p>
          <Button className="mt-4" onClick={() => setShowForm(true)}>
            <Plus className="size-4" /> Kreiraj prvu
          </Button>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {cycles.map((c: CycleSummary) => (
            <GroupTherapyCard
              key={c.id}
              cycle={c}
              onEdit={() => {
                void startEdit(c.id);
              }}
              onCancel={() => {
                if (confirm(`Otkazati ciklus "${c.name}"? Refundacije se obrađuju ručno u roku od 7 radnih dana.`)) {
                  cancelMutation.mutate(c.id);
                }
              }}
              onShowParticipants={() => setParticipantsOf(c)}
            />
          ))}
        </div>
      )}

      <GroupTherapyParticipantsDialog
        cycleId={participantsOf?.id ?? null}
        cycleName={participantsOf?.name ?? ""}
        onClose={() => setParticipantsOf(null)}
      />

    </div>
  );
}
